import { supabase } from './supabase';

/**
 * Statistiques de ventes EN LIGNE (payées par CB Stripe) d'un resto, pour
 * l'onglet « Paiements Stripe » : CA aujourd'hui / 7 jours / 30 jours, nombre
 * de commandes, ticket moyen. Agrégation PURE (testable) + un loader Supabase.
 *
 * « Vente en ligne payée » = order.payment_method === 'online'
 *   ET order.payment_status === 'paid'. (Le « payé sur place » n'est pas un
 *   encaissement Stripe ; il n'est pas compté ici.)
 */

export interface SalesBucket {
  cents: number;
  count: number;
}
export interface OnlineSalesSummary {
  today: SalesBucket;
  last7: SalesBucket;
  last30: SalesBucket;
  /** Ticket moyen sur 30 jours (centimes), 0 si aucune commande. */
  avgTicketCents: number;
}

interface OrderLite {
  total_cents: number;
  created_at: string;
  payment_method: string | null;
  payment_status: string | null;
}

const DAY_MS = 86_400_000;

/** Agrège les ventes en ligne payées par fenêtre temporelle (pur). */
export function summarizeOnlineSales(orders: OrderLite[], now: number = Date.now()): OnlineSalesSummary {
  const startToday = new Date(now);
  startToday.setHours(0, 0, 0, 0);
  const t0 = startToday.getTime();
  const w7 = now - 7 * DAY_MS;
  const w30 = now - 30 * DAY_MS;

  const today: SalesBucket = { cents: 0, count: 0 };
  const last7: SalesBucket = { cents: 0, count: 0 };
  const last30: SalesBucket = { cents: 0, count: 0 };

  for (const o of orders) {
    if (o.payment_method !== 'online' || o.payment_status !== 'paid') continue;
    const ts = Date.parse(o.created_at);
    if (Number.isNaN(ts)) continue;
    const c = Number(o.total_cents) || 0;
    if (ts >= w30) { last30.cents += c; last30.count += 1; }
    if (ts >= w7) { last7.cents += c; last7.count += 1; }
    if (ts >= t0) { today.cents += c; today.count += 1; }
  }
  const avgTicketCents = last30.count > 0 ? Math.round(last30.cents / last30.count) : 0;
  return { today, last7, last30, avgTicketCents };
}

/** Charge les commandes des 31 derniers jours et renvoie le résumé des ventes en ligne. */
export async function loadOnlineSales(tenantId: string): Promise<OnlineSalesSummary> {
  const since = new Date(Date.now() - 31 * DAY_MS).toISOString();
  const { data } = await supabase
    .from('orders')
    .select('total_cents, created_at, payment_method, payment_status')
    .eq('tenant_id', tenantId)
    .gte('created_at', since);
  return summarizeOnlineSales((data as OrderLite[] | null) ?? []);
}
